import { logger } from '../utils/logger';

// Types for moderation service
export interface ModerationResult {
  isAppropriate: boolean;
  confidence: number;
  reason?: string;
  detectedIssues?: string[];
  detectedWords?: string[]; // Array of detected profanity words
}

// Default patterns (will be loaded from JSON)
let MYANMAR_PROFANITY_PATTERNS: RegExp[] = [];
let ENGLISH_PROFANITY_PATTERNS: RegExp[] = [];
const SPAM_PATTERNS: RegExp[] = [
  /\b(https?:\/\/[^\s]+)\b/gi, // URLs
  /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, // Credit card numbers
  /(.{1,3})\1{4,}/g, // Excessive repetition
];
let THREAT_PATTERNS: RegExp[] = [];

// Load patterns from JSON
let patternsLoaded = false;

async function loadPatterns() {
  if (patternsLoaded) return;

  try {
    const profanityData = await import('../data/profanityPatterns.json');
    console.log('Profanity patterns loaded successfully:', profanityData.default);

    // Create regex patterns from JSON data
    MYANMAR_PROFANITY_PATTERNS = [
      // Basic profanity - exact matches from JSON
      ...profanityData.default.myanmarProfanity.map(
        (word) => new RegExp(word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'),
      ),
      // Pattern-based matches from JSON
      ...profanityData.default.myanmarProfanityPatterns.map((pattern) => new RegExp(pattern, 'gi')),
    ];
    console.log('Myanmar patterns loaded:', MYANMAR_PROFANITY_PATTERNS.length);

    // English profanity patterns from JSON
    ENGLISH_PROFANITY_PATTERNS = [
      ...profanityData.default.englishProfanity.map(
        (word) => new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi'),
      ),
    ];
    console.log('English patterns loaded:', ENGLISH_PROFANITY_PATTERNS.length);

    // Add threat patterns from JSON
    if (profanityData.default.threatPatterns) {
      THREAT_PATTERNS = profanityData.default.threatPatterns.map(
        (pattern: string) => new RegExp(pattern, 'gi'),
      );
      console.log('Threat patterns loaded:', THREAT_PATTERNS.length);
    }

    patternsLoaded = true;
    console.log('All profanity patterns loaded successfully');
  } catch (error) {
    console.error('Failed to load profanity patterns:', error);
  }
}

// Local moderation only - no external API calls needed

/**
 * Check text using local pattern matching
 * @param text - Text to check
 * @returns ModerationResult
 */
async function checkLocalPatterns(text: string): Promise<ModerationResult> {
  await loadPatterns();
  const detectedIssues: string[] = [];
  const detectedWords: string[] = [];
  let confidence = 0;

  // Check Myanmar profanity
  for (const pattern of MYANMAR_PROFANITY_PATTERNS) {
    // Reset regex lastIndex to avoid global flag issues
    pattern.lastIndex = 0;
    const matches = text.match(pattern);
    if (matches) {
      console.log(`🚨 Myanmar profanity detected: "${text}" matched pattern: ${pattern}`);
      detectedIssues.push('myanmar_profanity');
      detectedWords.push(...matches);
      confidence = Math.max(confidence, 0.9);
    }
  }

  // Check English profanity
  for (const pattern of ENGLISH_PROFANITY_PATTERNS) {
    // Reset regex lastIndex to avoid global flag issues
    pattern.lastIndex = 0;
    const matches = text.match(pattern);
    if (matches) {
      console.log(`🚨 English profanity detected: "${text}" matched pattern: ${pattern}`);
      detectedIssues.push('english_profanity');
      detectedWords.push(...matches);
      confidence = Math.max(confidence, 0.9);
    }
  }

  // Check spam patterns
  for (const pattern of SPAM_PATTERNS) {
    // Reset regex lastIndex to avoid global flag issues
    pattern.lastIndex = 0;
    if (pattern.test(text)) {
      detectedIssues.push('spam_content');
      confidence = Math.max(confidence, 0.7);
    }
  }

  const isAppropriate = detectedIssues.length === 0;

  return {
    isAppropriate,
    confidence: isAppropriate ? 0.8 : confidence,
    reason: isAppropriate ? undefined : getReasonFromIssues(detectedIssues),
    detectedIssues,
    detectedWords: detectedWords.length > 0 ? detectedWords : undefined,
  };
}

/**
 * Enhanced local pattern checking with additional validation
 * @param text - Text to check
 * @returns ModerationResult
 */
async function checkEnhancedLocalPatterns(text: string): Promise<ModerationResult> {
  const result = await checkLocalPatterns(text);

  // Additional checks for context and severity
  if (!result.isAppropriate) {
    // Increase confidence for multiple pattern matches
    if (result.detectedIssues && result.detectedIssues.length > 1) {
      result.confidence = Math.min(0.95, result.confidence + 0.1);
    }
  }

  return result;
}

/**
 * Get human-readable reason from detected issues
 * @param issues - Array of detected issues
 * @returns string
 */
function getReasonFromIssues(issues: string[]): string {
  if (
    issues.includes('myanmar_profanity') ||
    issues.includes('english_profanity') ||
    issues.includes('profanity')
  ) {
    return 'share.inappropriate_language';
  }
  if (issues.includes('spam_content')) {
    return 'share.spam_detected';
  }
  if (issues.includes('toxicity') || issues.includes('severe_toxicity')) {
    return 'share.toxic_content';
  }
  if (issues.includes('identity_attack')) {
    return 'share.hate_speech';
  }
  if (issues.includes('insult')) {
    return 'share.insulting_content';
  }
  if (issues.includes('threat')) {
    return 'share.threatening_content';
  }
  return 'share.inappropriate_content';
}

/**
 * Main moderation function
 * @param text - Text to moderate
 * @returns Promise<ModerationResult>
 */
export async function moderateText(text: string): Promise<ModerationResult> {
  if (!text || typeof text !== 'string' || text.trim().length === 0) {
    return {
      isAppropriate: true,
      confidence: 1.0,
    };
  }

  const trimmedText = text.trim();

  // Use enhanced local patterns for comprehensive checking
  const result = await checkEnhancedLocalPatterns(trimmedText);

  logger.log('Local moderation result:', {
    textLength: trimmedText.length,
    isAppropriate: result.isAppropriate,
    confidence: result.confidence,
    issues: result.detectedIssues,
    method: 'local_patterns_only',
  });

  return result;
}

/**
 * Moderate text with context (for ShareDialog)
 * @param text - Text to moderate
 * @param context - Context information
 * @returns Promise<ModerationResult>
 */
export async function moderateTextWithContext(
  text: string,
  context: { contentType: string; platform: string },
): Promise<ModerationResult> {
  const result = await moderateText(text);

  console.log(`🔍 Moderation result for "${text}" (${context.contentType}):`, result);

  // For share notes, be slightly more permissive only for very low-confidence detections
  // But still block high-confidence inappropriate content
  if (context.contentType === 'share_note' && !result.isAppropriate && result.confidence < 0.7) {
    console.log(`⚠️ Share note permissive mode: allowing low-confidence detection for "${text}"`);
    return {
      ...result,
      isAppropriate: true,
      reason: undefined,
    };
  }

  return result;
}

export default {
  moderateText,
  moderateTextWithContext,
};
